import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  ReactNode,
} from "react";
import { User } from "firebase";
import firebase from "./firebase";
import { AuthenticatedUser, getPublicUserProfile } from "../firebase/users";

interface Context {
  user: AuthenticatedUser | null;
  initializing: boolean;
  isSignedIn: boolean;
  isAdmin: boolean;
  signIn: (
    email: string,
    password: string,
    checkForAdmin?: boolean
  ) => Promise<AuthenticatedUser | null>;
  signOut: () => Promise<void>;
}

const initialValue = {
  user: null,
  initializing: true,
  isSignedIn: false,
  isAdmin: false,
  signIn: (email: string, password: string, checkForAdmin: boolean = false) =>
    Promise.resolve(null),
  signOut: () => Promise.resolve(),
};

const AuthContext = createContext<Context>(initialValue);

interface Props {
  children: ReactNode;
}

function AuthProvider(props: Props) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  const isSignedIn = user ? true : false;
  const isAdmin = user?.isAdmin ? true : false;

  const createAuthenticatedUser = useCallback(async (user: User) => {
    if (!user || !user.email) return null;

    // Fetch the user profile.
    const userProfile = await getPublicUserProfile(user.uid);
    if (!userProfile) return null;

    // Determine whether the user is an admin.
    const tokenResult = await user.getIdTokenResult();
    const isAdmin = tokenResult.claims.admin ? true : false;

    const authenticatedUser: AuthenticatedUser = {
      id: user.uid,
      email: user.email,
      isAdmin,
      displayname: userProfile.displayname,
      groupAccount: userProfile.groupaccount,
      avatar: userProfile.avatar,
    };

    return authenticatedUser;
  }, []);

  useEffect(() => {
    const unsubscribe = firebase
      .auth()
      .onAuthStateChanged(async (user: User | null) => {
        if (user) {
          const authenticatedUser = await createAuthenticatedUser(user);
          setUser(authenticatedUser);
          setInitializing(false);
        } else {
          setUser(null);
          setInitializing(false);
        }
      });

    return () => unsubscribe();
  }, [createAuthenticatedUser]);

  const signIn = async (
    email: string,
    password: string,
    checkForAdmin: boolean = false
  ) => {
    const response = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);

    let authenticatedUser = null;
    if (response.user) {
      authenticatedUser = await createAuthenticatedUser(response.user);

      if (checkForAdmin && !authenticatedUser?.isAdmin)
        throw new Error("Unauthorized");

      setUser(authenticatedUser);
    }
    return authenticatedUser;
  };

  const signOut = async () => {
    await firebase.auth().signOut();
    setUser(null);
  };

  const auth = {
    user,
    initializing,
    isSignedIn,
    isAdmin,
    signIn,
    signOut,
  };

  // Note how the auth context value is created above, but can be overridden by passing auth as props to the provider.
  return <AuthContext.Provider value={auth} {...props} />;
}

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error("Can not use 'useAuth' outside of a AuthProvider");

  return context;
};

export { AuthProvider, useAuth };
